import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, KeyRound } from "lucide-react";

export default function UserEdit({ u, roleOpts, sites, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [pw, setPw] = useState("");
  useEffect(() => {
    if (open) { setF({ name: u.name, email: u.email, role: u.role, home_location: u.home_location, cost_rate: u.cost_rate ?? 0, pay_rate: u.pay_rate ?? 0, approval_limit: u.approval_limit ?? 0, active: u.active }); setPw(""); }
  }, [open, u]);
  const locked = u.role === "management" && user.role !== "management";
  if (locked) return null;
  const save = async () => {
    try { await api.patch(`/users/${u.id}`, { ...f, ...(pw ? { password: pw } : {}) }); toast.success(pw ? `Saved. New password set for ${f.name}` : "Saved"); setOpen(false); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const inp = (k, l, type = "text") => (
    <div><Label className="text-xs">{l}</Label><Input type={type} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`edit-user-${k}`} className="mt-1" /></div>
  );
  const roles = u.role === "management" && user.role !== "management" ? ["management"] : roleOpts;
  const self = u.id === user.id;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`user-edit-${u.email}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline mr-3"><Pencil className="w-3.5 h-3.5" />Edit</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading uppercase">Edit {u.name}</DialogTitle>
            <DialogDescription>Changes are recorded in the audit trail.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {inp("name", "Name")}{inp("email", "Email (login)", "email")}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Role</Label>
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })} disabled={self}><SelectTrigger data-testid="edit-user-role" className="mt-1" title={self ? "You can't change your own user type" : undefined}><SelectValue /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Home site</Label>
                <Select value={f.home_location} onValueChange={(v) => setF({ ...f, home_location: v })}><SelectTrigger data-testid="edit-user-site" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{sites.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-3 gap-3">{inp("cost_rate", "Cost €/hr", "number")}{inp("pay_rate", "Pay €/hr", "number")}{inp("approval_limit", "Approve €", "number")}</div>
            {u.id !== user.id && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="edit-user-active" className="w-4 h-4 accent-orange-500" />Active (can sign in)</label>}
            <div className="rounded-lg border border-border p-3">
              <Label className="text-xs flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" />Reset password (leave blank to keep)</Label>
              <Input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password, min 8 characters" data-testid="edit-user-password" className="mt-1" />
              <p className="text-[11px] text-muted-foreground mt-1">They'll be signed out everywhere. Tell them the new password.</p>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!f.name || !f.email || (pw && pw.length < 8)} data-testid="edit-user-save" className="w-full font-bold uppercase">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
